import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bookmark, Plus, Star, Trash2, Users } from 'lucide-react';
import { useState } from 'react';
import { Button, IconButton } from '../../components/ui/Button.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { Checkbox, Field, TextInput } from '../../components/ui/Field.js';
import { ErrorBanner } from '../../components/ui/States.js';
import { viewsApi } from '../../lib/api.js';
import { cx } from '../../lib/cx.js';
import { errorMessage } from '../../lib/errors.js';
import type { SavedView } from '../../lib/schemas.js';
import { toast } from '../../lib/toast.js';
import { readDefaultViewId, writeDefaultViewId } from './defaultView.js';
import { filtersFromSavedView, savedViewPayload, type AlarmFilters } from './filters.js';

export interface SavedViewsProps {
  currentUserId: string | null;
  /** The caller's teams: a shared view must belong to one of them. */
  currentTeamIds: string[];
  filters: AlarmFilters;
  activeViewId: string | null;
  onApply: (view: SavedView, filters: AlarmFilters) => void;
  onClearView: () => void;
  className?: string;
}

/**
 * Saved views sidebar: "mine" and "shared", apply, save the current filters as
 * a new view, mark one as the default and delete your own.
 */
export function SavedViews({
  currentUserId,
  currentTeamIds,
  filters,
  activeViewId,
  onApply,
  onClearView,
  className,
}: SavedViewsProps) {
  const client = useQueryClient();
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState('');
  const [shared, setShared] = useState(false);
  const [defaultViewId, setDefaultViewId] = useState<string | null>(readDefaultViewId);

  const views = useQuery({ queryKey: ['views'], queryFn: () => viewsApi.list(), retry: false });

  // A shared view belongs to a team: the one the filters name, else the
  // caller's first. The API refuses a shared view without one.
  const shareTeamId = filters.team_id || currentTeamIds[0] || null;
  const create = useMutation({
    mutationFn: () => {
      if (shared && !shareTeamId) {
        return Promise.reject(new Error('Filter on a team, or join one, to share a view'));
      }
      return viewsApi.create(
        savedViewPayload(filters, {
          name: name.trim(),
          shared,
          teamId: shared ? shareTeamId : null,
        }),
      );
    },
    onSuccess: async () => {
      setSaveOpen(false);
      setName('');
      toast.success('View saved');
      await client.invalidateQueries({ queryKey: ['views'] });
    },
    onError: (error) =>
      toast.error('Could not save the view', { description: errorMessage(error) }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => viewsApi.remove(id),
    onSuccess: async () => {
      toast.success('View deleted');
      await client.invalidateQueries({ queryKey: ['views'] });
    },
    onError: (error) =>
      toast.error('Could not delete the view', { description: errorMessage(error) }),
  });

  const items = views.data?.items ?? [];
  const mine = items.filter(
    (view) => !view.shared && (!currentUserId || view.user_id === currentUserId),
  );
  const sharedViews = items.filter((view) => view.shared);

  const section = (title: string, icon: React.ReactNode, list: SavedView[], canDelete: boolean) => (
    <div>
      <p className="flex items-center gap-1.5 px-1 pb-1 text-[var(--text-xs)] font-semibold uppercase tracking-wide text-[var(--text-3)]">
        {icon}
        {title}
      </p>
      {list.length === 0 ? (
        <p className="px-1 pb-2 text-[var(--text-sm)] text-[var(--text-3)]">None yet.</p>
      ) : (
        <ul className="flex flex-col gap-0.5 pb-2">
          {list.map((view) => (
            <li key={view.id} className="group flex items-center gap-1">
              <button
                type="button"
                onClick={() => onApply(view, filtersFromSavedView(view))}
                className={cx(
                  'min-w-0 flex-1 truncate rounded-[var(--radius-sm)] px-2 py-1 text-left text-[var(--text-base)]',
                  activeViewId === view.id
                    ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent-ink)]'
                    : 'text-[var(--text-2)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]',
                )}
              >
                {view.name}
              </button>
              <button
                type="button"
                aria-label={
                  defaultViewId === view.id
                    ? `Unset ${view.name} as default view`
                    : `Set ${view.name} as default view`
                }
                onClick={() => {
                  const next = defaultViewId === view.id ? null : view.id;
                  setDefaultViewId(next);
                  writeDefaultViewId(next);
                }}
                className={cx(
                  'shrink-0 rounded-[var(--radius-sm)] p-1',
                  defaultViewId === view.id
                    ? 'text-[var(--accent-ink)]'
                    : 'text-[var(--text-3)] opacity-0 hover:text-[var(--text)] group-hover:opacity-100',
                )}
              >
                <Star
                  className="h-3.5 w-3.5"
                  fill={defaultViewId === view.id ? 'currentColor' : 'none'}
                />
              </button>
              {canDelete && (
                <IconButton
                  label={`Delete view ${view.name}`}
                  variant="ghost"
                  size="sm"
                  className="shrink-0 opacity-0 group-hover:opacity-100"
                  onClick={() => remove.mutate(view.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </IconButton>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return (
    <aside
      aria-label="Saved views"
      className={cx(
        'flex w-full flex-col gap-2 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] p-2',
        className,
      )}
    >
      <div className="flex items-center justify-between gap-2 px-1">
        <h2 className="text-[var(--text-base)] font-semibold text-[var(--text)]">Views</h2>
        <Button size="sm" variant="ghost" onClick={() => setSaveOpen(true)}>
          <Plus className="h-3.5 w-3.5" />
          Save
        </Button>
      </div>

      <button
        type="button"
        onClick={onClearView}
        className={cx(
          'rounded-[var(--radius-sm)] px-2 py-1 text-left text-[var(--text-base)]',
          activeViewId === null
            ? 'bg-[var(--accent-soft)] font-medium text-[var(--accent-ink)]'
            : 'text-[var(--text-2)] hover:bg-[var(--surface-2)]',
        )}
      >
        All alarms
      </button>

      {views.error ? (
        <ErrorBanner title="Saved views unavailable" error={views.error} />
      ) : (
        <>
          {section('Mine', <Bookmark className="h-3 w-3" />, mine, true)}
          {section('Shared', <Users className="h-3 w-3" />, sharedViews, false)}
        </>
      )}

      <Dialog
        open={saveOpen}
        onClose={() => setSaveOpen(false)}
        title="Save this view"
        description="The current filters, grouping and sort are stored under a name."
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!name.trim()}
              loading={create.isPending}
              onClick={() => create.mutate()}
            >
              Save view
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Field label="View name" required>
            {({ id, invalid }) => (
              <TextInput
                id={id}
                invalid={invalid}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Critical, unacknowledged"
              />
            )}
          </Field>
          <Checkbox
            label="Share with my teams"
            checked={shared}
            onChange={(event) => setShared(event.target.checked)}
          />
        </div>
      </Dialog>
    </aside>
  );
}
